import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  CHANNEL_SUBPROTOCOL_PREFIX,
  CHAT_SUBPROTOCOL,
  CHAT_WS_PATH,
  FrameType,
  IDLE_TIMEOUT_CLOSE_CODE,
  RESUME_SUBPROTOCOL_PREFIX,
  RSECRET_SUBPROTOCOL_PREFIX,
  TAssistantMessageFrame,
  TClosedFrame,
  TErrorFrame,
  THandoffStatusFrame,
  THistoryFrame,
  TLanguage,
  TOperatorMessageFrame,
  TReadyFrame,
  TServerFrame,
  TUserMessageFrame,
} from "@/models/chat.model";
import { BASE_URL, CHAT_CHANNEL } from "@/constants/common";

import { loadResumeSession } from "../helpers/resumeSession";
import { debugBus } from "../helpers/debugBus";

type TMeta = { socket: WebSocket };
type TDisconnectMeta = TMeta & { code: number; reason: string };

type SocketEventsHandlers = {
  onConnect?: (meta: TMeta) => void;
  onDisconnect?: (meta: TDisconnectMeta) => void;
  onReady?: (frame: TReadyFrame, meta: TMeta) => void;
  onHistory?: (frame: THistoryFrame, meta: TMeta) => void;
  onAssistantMessage?: (frame: TAssistantMessageFrame, meta: TMeta) => void;
  onOperatorMessage?: (frame: TOperatorMessageFrame, meta: TMeta) => void;
  onHandoffStatus?: (frame: THandoffStatusFrame, meta: TMeta) => void;
  onErrorFrame?: (frame: TErrorFrame, meta: TMeta) => void;
  onClosed?: (frame: TClosedFrame, meta: TMeta) => void;
  onError?: (error: string) => void;
};

const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 15000;

const toWsUrl = (base: string): string => {
  const origin = base
    ? base.replace(/^http/i, "ws")
    : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;

  return `${origin.replace(/\/+$/, "")}${CHAT_WS_PATH}`;
};

const buildSubprotocols = (): string[] => {
  const protocols = [
    `${CHANNEL_SUBPROTOCOL_PREFIX}${CHAT_CHANNEL}`,
    CHAT_SUBPROTOCOL,
  ];

  const resumeSession = loadResumeSession();
  if (resumeSession) {
    protocols.push(`${RESUME_SUBPROTOCOL_PREFIX}${resumeSession.conversationId}`);
    protocols.push(`${RSECRET_SUBPROTOCOL_PREFIX}${resumeSession.resumeSecret}`);
  }

  return protocols;
};

const NO_RECONNECT_CLOSE_CODES = new Set([1000, 1002, 1008, IDLE_TIMEOUT_CLOSE_CODE]);

const buildUserMessageFrame = (text: string, language?: TLanguage, debug = false): TUserMessageFrame => {
  const frame: TUserMessageFrame = { type: FrameType.USER_MESSAGE, text, debug };
  if (language) frame.language = language;
  return frame;
};

const decodeFrame = (raw: unknown): TServerFrame | null => {
  if (typeof raw !== "string") return null;

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (
      parsed &&
      typeof parsed === "object" &&
      "type" in parsed &&
      typeof (parsed as { type: unknown }).type === "string"
    ) {
      return parsed as TServerFrame;
    }

    return null;
  } catch {
    return null;
  }
};

export const useSocketIO = (params: SocketEventsHandlers) => {
  const socketRef = useRef<WebSocket | null>(null);
  const eventsRef = useRef(params);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUnmountedRef = useRef(false);
  const connectRef = useRef<() => void>(() => {});
  const clearReconnectTimerRef = useRef<() => void>(() => {});

  useEffect(() => {
    eventsRef.current = params;
  }, [params]);

  useEffect(() => {
    isUnmountedRef.current = false;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
    clearReconnectTimerRef.current = clearReconnectTimer;

    const scheduleReconnect = () => {
      if (isUnmountedRef.current) return;

      const delay = Math.min(
        RECONNECT_BASE_DELAY * 2 ** reconnectAttemptsRef.current,
        RECONNECT_MAX_DELAY,
      );
      reconnectAttemptsRef.current += 1;
      debugBus.log("system", "reconnect_scheduled", { attempt: reconnectAttemptsRef.current, delay });
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    const handleMessage = (event: MessageEvent) => {
      const frame = decodeFrame(event.data);
      const socket = socketRef.current;

      if (!frame) {
        debugBus.log("in", "undecodable", event.data);
        return;
      }
      debugBus.log("in", frame.type, frame);

      if (!socket) return;

      const meta: TMeta = { socket };

      switch (frame.type) {
        case FrameType.READY:
          eventsRef.current.onReady?.(frame, meta);
          break;
        case FrameType.HISTORY:
          eventsRef.current.onHistory?.(frame, meta);
          break;
        case FrameType.ASSISTANT_MESSAGE:
          eventsRef.current.onAssistantMessage?.(frame, meta);
          break;
        case FrameType.OPERATOR_MESSAGE:
          eventsRef.current.onOperatorMessage?.(frame, meta);
          break;
        case FrameType.HANDOFF_STATUS:
          eventsRef.current.onHandoffStatus?.(frame, meta);
          break;
        case FrameType.ERROR:
          eventsRef.current.onErrorFrame?.(frame, meta);
          break;
        case FrameType.CLOSED:
          eventsRef.current.onClosed?.(frame, meta);
          break;
        default:
          break;
      }
    };

    const connect = () => {
      if (isUnmountedRef.current) return;

      const socket = new WebSocket(toWsUrl(BASE_URL), buildSubprotocols());
      socketRef.current = socket;

      // Lets the Debugger panel push a hand-written raw
      // frame straight through the live socket.
      debugBus.registerSender((raw) => {
        if (socket.readyState !== WebSocket.OPEN) return false;
        socket.send(raw);
        debugBus.log("out", "raw", raw);
        return true;
      });

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0;
        debugBus.log("system", "connect", {});
        eventsRef.current.onConnect?.({ socket });
      };

      socket.onmessage = handleMessage;

      socket.onerror = () => {
        debugBus.log("system", "error", {});
        eventsRef.current.onError?.("WebSocket error");
      };

      socket.onclose = (event: CloseEvent) => {
        debugBus.log("system", "disconnect", { code: event.code, reason: event.reason });
        eventsRef.current.onDisconnect?.({ socket, code: event.code, reason: event.reason });
        if (!NO_RECONNECT_CLOSE_CODES.has(event.code)) {
          scheduleReconnect();
        }
      };
    };
    connectRef.current = connect;

    connect();

    return () => {
      isUnmountedRef.current = true;
      clearReconnectTimer();
      debugBus.registerSender(null);

      const socket = socketRef.current;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
      }
      socketRef.current = null;
    };
  }, []);

  const sendMessage = useCallback((text: string, language?: TLanguage, debug = false) => {
    const socket = socketRef.current;

    if (socket?.readyState === WebSocket.OPEN) {
      const frame = buildUserMessageFrame(text, language, debug);
      debugBus.log("out", frame.type, frame);
      socket.send(JSON.stringify(frame));
    }
  }, []);

  const reconnect = useCallback(() => {
    clearReconnectTimerRef.current();
    reconnectAttemptsRef.current = 0;

    const socket = socketRef.current;
    if (socket) {
      socket.onclose = null;
      socket.close();
    }

    connectRef.current();
  }, []);

  return useMemo(
    () => ({
      socket: socketRef.current,
      sendMessage,
      reconnect,
    }),
    [sendMessage, reconnect],
  );
};
